/**
 * Command strip content for the Loader mode: missions by family, antennas by
 * station, awareness layers, overlays and deep-space bodies. Checkboxes drive
 * globe visibility (not the analysis; that is the What-if panel). Clicking a
 * name selects it everywhere and flies the camera there. Each row is a
 * checkbox beside a button (never one inside the other), sized by the density
 * variables.
 */
import { useMemo, useState } from 'react';
import {
  Eye,
  EyeOff,
  Layers,
  RadioTower,
  Satellite,
  Sparkles,
  Telescope,
  type LucideIcon,
} from 'lucide-react';
import { useUI } from '@/state/store';
import { cn } from '@/lib/cn';
import { HORIZONS_BODIES, OVERLAYS } from '../constants';
import { getGlobeEngine } from '../globe/engineHandle';
import { useLoader } from '../store';
import type { Antenna, Mission } from '../types';
import { tierColor } from './format';
import { CatalogueStrip } from '../ssa/Strip';

const FAMILY: Record<string, string> = {
  suomi_npp: 'JPSS',
  noaa20: 'JPSS',
  noaa21: 'JPSS',
  metop_b: 'MetOp',
  metop_c: 'MetOp',
  metop_sg_a1: 'MetOp',
  goes18: 'GOES',
  goes19: 'GOES',
  goes16: 'GOES',
  geo_kompsat_2a: 'GEO-KOMPSAT',
  geo_kompsat_2b: 'GEO-KOMPSAT',
  kompsat2: 'KOMPSAT',
  kompsat3: 'KOMPSAT',
  kompsat3a: 'KOMPSAT',
  kompsat5: 'KOMPSAT',
  kompsat7: 'KOMPSAT',
  jason3: 'Jason',
  dmsp_f18: 'DMSP',
  dmsp_f16: 'DMSP',
  paz: 'SAR',
  solar1: 'Solar/L1',
  ace: 'Solar/L1',
  dscovr: 'Solar/L1',
  tdrs_11: 'TDRS',
  tdrs_12: 'TDRS',
  tdrs_13: 'TDRS',
};

type Section = 'missions' | 'antennas' | 'layers';

interface MissionGroup {
  key: string;
  label: string;
  items: Mission[];
}

interface AntennaGroup {
  key: string;
  label: string;
  items: Antenna[];
}

function groupMissions(missions: Mission[]): MissionGroup[] {
  const map = new Map<string, Mission[]>();
  for (const m of missions) {
    const family = m.catalogue?.family ?? FAMILY[m.id] ?? (missions.length > 60 ? m.agency || 'Other' : 'Other');
    if (!map.has(family)) map.set(family, []);
    map.get(family)!.push(m);
  }
  return [...map.entries()]
    .sort(([a], [b]) => (a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b)))
    .map(([label, items]) => ({
      key: label,
      label,
      items: [...items].sort(
        (x, y) => x.priorityTier - y.priorityTier || x.shortName.localeCompare(y.shortName),
      ),
    }));
}

function groupAntennas(antennas: Antenna[]): AntennaGroup[] {
  const map = new Map<string, { country: string; station: string; items: Antenna[] }>();
  for (const a of antennas) {
    const country = (a.location.country || 'Other').trim();
    const station = (a.location.name || 'Other').trim();
    const key = `${country}|${station}`;
    if (!map.has(key)) map.set(key, { country, station, items: [] });
    map.get(key)!.items.push(a);
  }
  return [...map.entries()]
    .sort(([, a], [, b]) => a.country.localeCompare(b.country) || a.station.localeCompare(b.station))
    .map(([key, g]) => ({
      key,
      label: `${g.station}, ${g.country}`,
      items: [...g.items].sort((x, y) => (y.capabilities.diameterM ?? 0) - (x.capabilities.diameterM ?? 0)),
    }));
}

function SectionButton({
  icon: Icon,
  label,
  count,
  active,
  expanded,
  open,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  count?: number;
  active: boolean;
  expanded: boolean;
  open: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={expanded ? undefined : label}
      aria-expanded={open}
      data-strip-section=""
      onClick={onClick}
      className={cn(
        'flex h-11 w-full items-center gap-3 px-[23px] text-left text-xs font-medium tracking-wide transition hover:bg-overlay focus-visible:outline-offset-[-2px]',
        active ? 'text-accent' : 'text-ink-2',
      )}
    >
      <Icon aria-hidden className="h-[18px] w-[18px] shrink-0" />
      {expanded && <span className="truncate">{label}</span>}
      {expanded && count !== undefined && <span className="ml-auto text-[10px] text-ink-3">{count}</span>}
    </button>
  );
}

/** Show all or none, as a labelled pair. */
function AllNone({ what, onAll, onNone }: { what: string; onAll: () => void; onNone: () => void }) {
  return (
    <div
      role="group"
      aria-label={`${what} on the globe`}
      className="flex gap-2 py-1 pl-4 text-[10px] uppercase tracking-wider text-ink-3"
    >
      <button type="button" className="rounded-control hover:text-ink" onClick={onAll}>
        All
      </button>
      <button type="button" className="rounded-control hover:text-ink" onClick={onNone}>
        None
      </button>
    </div>
  );
}

function VisibilityBox({
  checked,
  indeterminate,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      ref={(el) => {
        if (el) el.indeterminate = Boolean(indeterminate) && !checked;
      }}
      onChange={(e) => onChange(e.target.checked)}
      onClick={(e) => e.stopPropagation()}
      className="h-3.5 w-3.5 shrink-0 cursor-pointer accent-[var(--color-accent)]"
    />
  );
}

/**
 * `onOpenRequest` asks the strip to float open (a category pressed while collapsed: keyboard or
 * touch); `onPicked` tells it a mission or antenna was chosen, so a tablet overlay can close.
 */
export function LoaderStrip(props: { expanded: boolean; onOpenRequest: () => void; onPicked: () => void }) {
  const network = useLoader(s => s.network);
  return network === 'full_catalogue' ? <CatalogueStrip {...props} /> : <NetworkLoaderStrip {...props} />;
}
function NetworkLoaderStrip({
  expanded,
  onOpenRequest,
  onPicked,
}: {
  expanded: boolean;
  onOpenRequest: () => void;
  onPicked: () => void;
}) {
  const dataset = useLoader((s) => s.dataset);
  const visibleMissionIds = useLoader((s) => s.visibleMissionIds);
  const visibleAntennaIds = useLoader((s) => s.visibleAntennaIds);
  const setVisibleMissionIds = useLoader((s) => s.setVisibleMissionIds);
  const setVisibleAntennaIds = useLoader((s) => s.setVisibleAntennaIds);
  const selection = useUI((s) => s.selection);
  const select = useUI((s) => s.select);
  const profile = useLoader((s) => s.profile);
  const [open, setOpen] = useState<Section>('missions');

  const missionGroups = useMemo(() => groupMissions(dataset?.missions ?? []), [dataset]);
  const antennaGroups = useMemo(() => groupAntennas(dataset?.antennas ?? []), [dataset]);
  const tiers = dataset?.tiers ?? [];

  const missionVisible = (id: string) => !visibleMissionIds || visibleMissionIds.includes(id);
  const antennaVisible = (id: string) => !visibleAntennaIds || visibleAntennaIds.includes(id);
  const allMissionIds = dataset?.missions.map((m) => m.id) ?? [];
  const allAntennaIds = dataset?.antennas.map((a) => a.id) ?? [];

  const setMissionVisible = (ids: string[], visible: boolean) => {
    const current = new Set(visibleMissionIds ?? allMissionIds);
    for (const id of ids) {
      if (visible) current.add(id);
      else current.delete(id);
    }
    setVisibleMissionIds(current.size === allMissionIds.length ? null : [...current]);
  };
  const setAntennaVisible = (ids: string[], visible: boolean) => {
    const current = new Set(visibleAntennaIds ?? allAntennaIds);
    for (const id of ids) {
      if (visible) current.add(id);
      else current.delete(id);
    }
    setVisibleAntennaIds(current.size === allAntennaIds.length ? null : [...current]);
  };

  const toggle = (s: Section) => {
    if (!expanded) {
      onOpenRequest();
      setOpen(s);
    } else setOpen((o) => (o === s ? ('' as Section) : s));
  };

  return (
    <div className="flex-1 overflow-y-auto overflow-x-hidden py-2 scrollbar-thin">
      <SectionButton
        icon={Satellite}
        label="Missions"
        count={dataset?.missions.length}
        active={Boolean(selection.missionId)}
        expanded={expanded}
        open={expanded && open === 'missions'}
        onClick={() => toggle('missions')}
      />
      {expanded && open === 'missions' && (
        <div className="mb-1 ml-4 border-l border-hairline">
          <AllNone
            what="Missions"
            onAll={() => setVisibleMissionIds(null)}
            onNone={() => setVisibleMissionIds([])}
          />
          {missionGroups.map((g) => {
            const visibleCount = g.items.filter((m) => missionVisible(m.id)).length;
            return (
              <div key={g.key}>
                <div className="flex items-center gap-2 py-cell-y pl-4 pr-3 text-data-sm font-semibold text-ink-2">
                  <VisibilityBox
                    label={`Show ${g.label}`}
                    checked={visibleCount === g.items.length}
                    indeterminate={visibleCount > 0}
                    onChange={(v) =>
                      setMissionVisible(
                        g.items.map((m) => m.id),
                        v,
                      )
                    }
                  />
                  <span className="truncate">{g.label}</span>
                  <span className="ml-auto text-[10px] font-normal text-ink-3">{g.items.length}</span>
                </div>
                <ul>
                  {g.items.map((m) => {
                    const active = m.id === selection.missionId;
                    const visible = missionVisible(m.id);
                    return (
                      <li key={m.id} className="flex items-center gap-2 pl-7 hover:bg-overlay">
                        <VisibilityBox
                          label={`Show ${m.shortName}`}
                          checked={visible}
                          onChange={(v) => setMissionVisible([m.id], v)}
                        />
                        <button
                          type="button"
                          title={`${m.name} (tier ${m.priorityTier})`}
                          aria-current={active ? 'true' : undefined}
                          onClick={() => {
                            select({ missionId: m.id, antennaId: null, assetId: null });
                            getGlobeEngine()?.flyToMission(m.id);
                            onPicked();
                          }}
                          className={cn(
                            'flex min-w-0 flex-1 items-center gap-2 py-cell-y pr-3 text-left text-data transition focus-visible:outline-offset-[-2px]',
                            active ? 'font-semibold text-accent' : visible ? 'text-ink-2' : 'text-ink-3',
                          )}
                        >
                          <span
                            aria-hidden
                            className="h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{ background: tierColor(tiers, m.priorityTier) }}
                          />
                          <span className="truncate">{m.shortName}</span>
                          <span className="ml-auto text-data-sm text-ink-3">T{m.priorityTier}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      <SectionButton
        icon={RadioTower}
        label="Antennas"
        count={dataset?.antennas.length}
        active={Boolean(selection.antennaId)}
        expanded={expanded}
        open={expanded && open === 'antennas'}
        onClick={() => toggle('antennas')}
      />
      {expanded && open === 'antennas' && (
        <div className="mb-1 ml-4 border-l border-hairline">
          <AllNone
            what="Antennas"
            onAll={() => setVisibleAntennaIds(null)}
            onNone={() => setVisibleAntennaIds([])}
          />
          {antennaGroups.map((g) => {
            const visibleCount = g.items.filter((a) => antennaVisible(a.id)).length;
            return (
              <div key={g.key}>
                <div className="flex items-center gap-2 py-cell-y pl-4 pr-3 text-data-sm font-semibold text-ink-2">
                  <VisibilityBox
                    label={`Show ${g.label}`}
                    checked={visibleCount === g.items.length}
                    indeterminate={visibleCount > 0}
                    onChange={(v) =>
                      setAntennaVisible(
                        g.items.map((a) => a.id),
                        v,
                      )
                    }
                  />
                  <span className="truncate">{g.label}</span>
                  <span className="ml-auto text-[10px] font-normal text-ink-3">{g.items.length}</span>
                </div>
                <ul>
                  {g.items.map((a) => {
                    const active = a.id === selection.antennaId;
                    const visible = antennaVisible(a.id);
                    const disabled = a.enabled === false;
                    return (
                      <li key={a.id} className="flex items-center gap-2 pl-7 hover:bg-overlay">
                        <VisibilityBox
                          label={`Show ${a.shortName}`}
                          checked={visible}
                          onChange={(v) => setAntennaVisible([a.id], v)}
                        />
                        <button
                          type="button"
                          title={disabled ? `${a.name} (disabled)` : a.name}
                          aria-current={active ? 'true' : undefined}
                          onClick={() => {
                            select({ antennaId: a.id, missionId: null, assetId: null });
                            getGlobeEngine()?.flyToAntenna(a.id);
                            onPicked();
                          }}
                          className={cn(
                            'flex min-w-0 flex-1 items-center gap-2 py-cell-y pr-3 text-left text-data transition focus-visible:outline-offset-[-2px]',
                            active ? 'font-semibold text-accent' : visible ? 'text-ink-2' : 'text-ink-3',
                          )}
                        >
                          {/* Enabled is a filled dot, disabled a hollow ring: shape as well as colour. */}
                          <span
                            aria-hidden
                            className={cn(
                              'h-1.5 w-1.5 shrink-0 rounded-full',
                              disabled ? 'border border-offline bg-transparent' : 'bg-nominal',
                            )}
                          />
                          <span className="truncate">{a.shortName}</span>
                          {disabled && <span className="sr-only">(disabled)</span>}
                          <span className="ml-auto text-data-sm text-ink-3">
                            {a.capabilities.diameterM ? `${a.capabilities.diameterM} m` : ''}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
      {profile === 'full' && (
        <>
          <SectionButton
            icon={Layers}
            label="Layers"
            active={false}
            expanded={expanded}
            open={expanded && open === 'layers'}
            onClick={() => toggle('layers')}
          />
          {expanded && open === 'layers' && <LayerList />}
        </>
      )}
    </div>
  );
}

function LayerToggle({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <label
      className="flex cursor-pointer items-center gap-2 py-cell-y pl-4 pr-3 text-data text-ink-2 hover:bg-overlay"
      title={hint}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 accent-[var(--color-accent)]"
      />
      {checked ? (
        <Eye aria-hidden className="h-3 w-3 text-accent" />
      ) : (
        <EyeOff aria-hidden className="h-3 w-3 text-ink-3" />
      )}
      <span className="truncate">{label}</span>
    </label>
  );
}

function LayerList() {
  const s = useLoader();
  return (
    <div className="mb-1 ml-4 border-l border-hairline">
      <div className="flex items-center gap-2 py-1 pl-4 text-[11px] font-semibold text-ink-2">
        <Sparkles className="h-3 w-3" /> Awareness
      </div>
      <LayerToggle
        label="Aurora oval (OVATION)"
        checked={s.auroraEnabled}
        onChange={(v) => s.setView({ auroraEnabled: v })}
      />
      <LayerToggle
        label="Conjunction watch"
        checked={s.conjunctionsEnabled}
        onChange={(v) => s.setView({ conjunctionsEnabled: v })}
      />
      <LayerToggle
        label="Space weather"
        checked={s.spaceWeatherEnabled}
        onChange={(v) => s.setView({ spaceWeatherEnabled: v })}
      />
      <LayerToggle
        label="Upcoming launches"
        checked={s.launchesEnabled}
        onChange={(v) => s.setView({ launchesEnabled: v })}
      />
      <LayerToggle
        label="SatNOGS observations"
        checked={s.satnogsEnabled}
        onChange={(v) => s.setView({ satnogsEnabled: v })}
      />
      <LayerToggle
        label="GPS interference"
        checked={s.gpsJamEnabled}
        onChange={(v) => s.setView({ gpsJamEnabled: v })}
      />
      <LayerToggle
        label="TDRSS relay satellites"
        checked={s.tdrssShown}
        onChange={(v) => s.setView({ tdrssShown: v })}
      />
      <LayerToggle
        label="Night lights"
        checked={s.nightLights && s.dayNight}
        onChange={(v) => s.setView({ nightLights: v, ...(v ? { dayNight: true } : {}) })}
      />

      <div className="mt-1 flex items-center gap-2 py-1 pl-4 text-[11px] font-semibold text-ink-2">
        <Satellite className="h-3 w-3" /> Constellation overlays
      </div>
      {OVERLAYS.map((o) => (
        <LayerToggle
          key={o.key}
          label={o.label}
          checked={s.overlays.includes(o.key)}
          onChange={() => s.toggleOverlay(o.key)}
        />
      ))}

      <div className="mt-1 flex items-center gap-2 py-1 pl-4 text-[11px] font-semibold text-ink-2">
        <Telescope className="h-3 w-3" /> Deep space (JPL Horizons)
      </div>
      {HORIZONS_BODIES.map((b) => (
        <LayerToggle
          key={b.key}
          label={b.label}
          checked={s.horizonsBodies.includes(b.key)}
          onChange={() => s.toggleHorizonsBody(b.key)}
        />
      ))}

      <p className="px-4 py-2 text-[11px] text-ink-3">
        Weather imagery is in the cloud icon on the map toolbar.
      </p>
    </div>
  );
}
